<template>
    <div
        class="progress-circle"
        :style="{ width: diameter + 'px', height: diameter + 'px' }"
    >
        <svg :viewBox="'0 0 ' + diameter + ' ' + diameter">
            <!-- Border Circle -->
            <circle
                class="border"
                :cx="center"
                :cy="center"
                :r="radius"
                :stroke-width="strokeWidth"
                :stroke="circleBgColor"
            />
            <!-- Background Circle -->
            <circle
                class="background"
                :cx="center"
                :cy="center"
                :r="radius - 1"
                :stroke-width="strokeWidth"
            />
            <!-- Inner Border Circle -->
            <circle
                class="border"
                :cx="center"
                :cy="center"
                :r="radius - strokeWidth / 2"
                :stroke-width="1"
                :stroke="circleBgColor"
            />
            <!-- Progress Circle -->
            <circle
                class="progress"
                :cx="center"
                :cy="center"
                :r="radius"
                :stroke-width="strokeWidth"
                :stroke-dasharray="circumference"
                :stroke-dashoffset="isValid ? dashOffset : circumference"
                :stroke="progressColor"
            />
            <!-- Percentage Text -->
            <text
                :x="center"
                :y="center"
                text-anchor="middle"
                dy=".3em"
                class="percentage"
                :style="{ fontSize: fontSize }"
            >
                <tspan class="number">
                    {{
                        typeof percentage === 'number' &&
                        !isNaN(percentage) &&
                        percentage >= 0
                            ? roundedPercentage
                            : '—'
                    }}
                </tspan>
                <tspan
                    v-if="
                        typeof percentage === 'number' &&
                        !isNaN(percentage) &&
                        percentage >= 0
                    "
                    class="percent-symbol"
                >
                    {{ '%' }}
                </tspan>
            </text>
        </svg>
    </div>
</template>

<script>
import { getScoreColor } from '@/utils/scoreUtils';

export default {
    props: {
        percentage: {
            type: Number,
            required: true,
        },
        // Overall size of the SVG container (in pixels)
        diameter: {
            type: Number,
            default: 110,
        },
        // Thickness of the circle stroke
        strokeWidth: {
            type: Number,
            default: 20,
        },
        fontSize: {
            type: String,
            default: '1.5625rem',
        },
        circleBg: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            animatedPercentage: 0,
        };
    },
    computed: {
        roundedPercentage() {
            return Math.round(this.percentage);
        },
        center() {
            return this.diameter / 2;
        },
        radius() {
            return (this.diameter - this.strokeWidth) / 2;
        },
        circumference() {
            return 2 * Math.PI * this.radius;
        },
        dashOffset() {
            return this.circumference * (1 - this.animatedPercentage / 100);
        },
        progressColor() {
            return getScoreColor(this.animatedPercentage);
        },
        circleBgColor() {
            return this.circleBg || 'var(--neutral-500, #f1f1f1)';
        },
        isValid() {
            return (
                typeof this.percentage === 'number' &&
                !isNaN(this.percentage) &&
                this.percentage >= 0
            );
        },
    },
    mounted() {
        // Trigger the CSS transition on mount
        requestAnimationFrame(() => {
            this.animatedPercentage = this.percentage;
        });
    },
    watch: {
        // Optional: Allow updates after initial load
        percentage(newVal) {
            this.animatedPercentage = newVal;
        },
    },
};
</script>

<style scoped>
.progress-circle {
    flex-shrink: 0;
}
svg {
    width: 100%;
    height: 100%;
}
.border {
    fill: none;
}
.background {
    fill: none;
    stroke: none;
}
.progress {
    fill: none;
    transition: stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1), stroke 0.5s;
    stroke-linecap: butt;
}
.percentage {
    font-family: Montserrat;
    font-weight: 700;
    /* It's common to set fill on the text instead of color in SVG */
    fill: var(--primary-500);
}
.percent-symbol {
    fill: var(--primary-300);
}
</style>
